// ============================================
// Analytics Page - Spending, win rate, competitor tracking
// ============================================

import { useState, useEffect, useMemo } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  SpendingAnalytics,
  MonthlySpend,
  CompetitorProfile,
  DomainCategory,
} from "../../types";
import { DropCatchService } from "../../services/dropcatchService";

interface AnalyticsProps {
  dropcatchService: DropCatchService;
  categories: DomainCategory[];
  className?: string;
}

// Chart colors matching terminal theme
const CHART_COLORS = {
  primary: "#22c55e",
  secondary: "#16a34a",
  tertiary: "#15803d",
  warning: "#f59e0b",
  danger: "#ef4444",
  grid: "#166534",
  text: "#4ade80",
  muted: "#14532d",
};

const PIE_COLORS = [
  "#22c55e",
  "#3b82f6",
  "#8b5cf6",
  "#f59e0b",
  "#ef4444",
  "#06b6d4",
  "#ec4899",
  "#84cc16",
];

/**
 * Analytics Component
 *
 * Comprehensive analytics dashboard with:
 * - Monthly spending chart
 * - Win rate statistics
 * - ROI tracking
 * - Competitor analysis
 * - Category breakdown
 */
export function Analytics({
  dropcatchService,
  categories,
  className = "",
}: AnalyticsProps) {
  // State
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState<SpendingAnalytics | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<"6m" | "12m" | "all">("12m");
  const [selectedView, setSelectedView] = useState<"overview" | "competitors" | "categories">("overview");

  // Fetch analytics data
  useEffect(() => {
    loadAnalytics();
  }, [selectedPeriod]);

  const loadAnalytics = async () => {
    setLoading(true);
    try {
      // Fetch history to build analytics
      const historyResult = await dropcatchService.getAuctionHistory({
        size: 500,
        sort: "UpdatedDesc",
      });

      // Process history into analytics
      const processed = processHistoryToAnalytics(historyResult.items || []);
      setAnalytics(processed);
    } catch (error) {
      console.error("Failed to load analytics:", error);
    } finally {
      setLoading(false);
    }
  };

  // Process history data into analytics format
  const processHistoryToAnalytics = (history: any[]): SpendingAnalytics => {
    const monthlyMap = new Map<string, { amount: number; count: number }>();
    const competitorMap = new Map<string, CompetitorProfile>();
    const categorySpend: Record<string, { count: number; spent: number }> = {};

    let totalSpent = 0;
    let wins = 0;
    let losses = 0;
    let totalBids = 0;
    let winningBidTotal = 0;

    history.forEach((item) => {
      const date = new Date(item.updatedDate || item.createdDate);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

      // Track monthly spending
      if (item.status === "AuctionWon" || item.status === "Purchased") {
        const existing = monthlyMap.get(monthKey) || { amount: 0, count: 0 };
        monthlyMap.set(monthKey, {
          amount: existing.amount + (item.amount || 0),
          count: existing.count + 1,
        });
        totalSpent += item.amount || 0;
        wins++;
        winningBidTotal += item.amount || 0;

        // Track category
        const domain = item.domain || "";
        const matchedCategory = findCategoryForDomain(domain, categories);
        if (matchedCategory) {
          categorySpend[matchedCategory.name] = categorySpend[matchedCategory.name] || {
            count: 0,
            spent: 0,
          };
          categorySpend[matchedCategory.name].count++;
          categorySpend[matchedCategory.name].spent += item.amount || 0;
        }
      } else if (item.status === "AuctionLost") {
        losses++;
      }

      totalBids++;

      // Track competitors from bidders
      if (item.bidders) {
        item.bidders.forEach((bidderId: number) => {
          if (bidderId) {
            const existing = competitorMap.get(String(bidderId));
            if (existing) {
              existing.encounterCount++;
              existing.lastSeen = item.updatedDate;
              existing.domainsCompeted.push(item.domain);
            } else {
              competitorMap.set(String(bidderId), {
                alias: `Bidder ${bidderId}`,
                accountId: bidderId,
                encounterCount: 1,
                avgBid: item.amount || 0,
                lastSeen: item.updatedDate,
                domainsCompeted: [item.domain],
              });
            }
          }
        });
      }
    });

    // Convert monthly map to sorted array
    const monthlySpend: MonthlySpend[] = Array.from(monthlyMap.entries())
      .map(([month, data]) => ({
        month,
        amount: data.amount,
        count: data.count,
      }))
      .sort((a, b) => a.month.localeCompare(b.month));

    // Filter by period
    const now = new Date();
    const filteredMonthly = monthlySpend.filter((m) => {
      const monthDate = new Date(m.month + "-01");
      const monthsAgo = (now.getTime() - monthDate.getTime()) / (1000 * 60 * 60 * 24 * 30);
      if (selectedPeriod === "6m") return monthsAgo <= 6;
      if (selectedPeriod === "12m") return monthsAgo <= 12;
      return true;
    });

    // Get top competitors
    const topCompetitors = Array.from(competitorMap.values())
      .sort((a, b) => b.encounterCount - a.encounterCount)
      .slice(0, 10);

    return {
      monthlySpend: filteredMonthly,
      totalSpent,
      winRate: totalBids > 0 ? (wins / totalBids) * 100 : 0,
      totalWins: wins,
      totalLosses: losses,
      avgBidAmount: totalBids > 0 ? totalSpent / totalBids : 0,
      avgWinningBid: wins > 0 ? winningBidTotal / wins : 0,
      topCompetitors,
      categoryBreakdown: categorySpend,
    };
  };

  // Find category for domain
  const findCategoryForDomain = (domain: string, cats: DomainCategory[]) => {
    const lowerDomain = domain.toLowerCase();
    for (const cat of cats) {
      for (const keyword of cat.keywords) {
        if (lowerDomain.includes(keyword)) {
          return cat;
        }
      }
    }
    return null;
  };

  // Category chart data
  const categoryChartData = useMemo(() => {
    if (!analytics) return [];
    return Object.entries(analytics.categoryBreakdown).map(([name, data]) => ({
      name,
      value: data.spent,
      count: data.count,
    }));
  }, [analytics]);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-black border-2 border-green-500 p-3">
          <p className="text-green-400 font-bold">{label}</p>
          {payload.map((entry: any, index: number) => (
            <p key={index} className="text-green-500 text-sm">
              {entry.name}: ${entry.value?.toLocaleString() || 0}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className={`p-6 ${className}`}>
        <div className="text-center">
          <span className="text-green-500 animate-pulse tracking-wider">
            LOADING ANALYTICS DATA...
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col h-full bg-black ${className}`}>
      {/* Header */}
      <div className="px-6 py-3 border-b-2 border-green-500 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 bg-green-500 animate-pulse" />
            <span className="text-green-400 font-bold tracking-wider">
              ANALYTICS DASHBOARD
            </span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* Period selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-green-600">PERIOD:</span>
            {(["6m", "12m", "all"] as const).map((period) => (
              <button
                key={period}
                onClick={() => setSelectedPeriod(period)}
                className={`px-2 py-1 border text-xs ${
                  selectedPeriod === period
                    ? "border-green-500 bg-green-500 text-black"
                    : "border-green-700 text-green-600 hover:border-green-500"
                }`}
              >
                {period.toUpperCase()}
              </button>
            ))}
          </div>
          {/* View selector */}
          <div className="flex items-center gap-2">
            {(["overview", "competitors", "categories"] as const).map((view) => (
              <button
                key={view}
                onClick={() => setSelectedView(view)}
                className={`px-3 py-1 border text-xs ${
                  selectedView === view
                    ? "border-green-500 bg-green-500 text-black"
                    : "border-green-700 text-green-600 hover:border-green-500"
                }`}
              >
                {view.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="px-6 py-4 border-b border-green-800 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <StatCard
          label="TOTAL SPENT"
          value={`$${analytics?.totalSpent?.toLocaleString() || 0}`}
          color="green"
        />
        <StatCard
          label="WIN RATE"
          value={`${analytics?.winRate?.toFixed(1) || 0}%`}
          color={analytics?.winRate && analytics.winRate >= 50 ? "green" : "yellow"}
        />
        <StatCard
          label="TOTAL WINS"
          value={analytics?.totalWins?.toString() || "0"}
          color="green"
        />
        <StatCard
          label="TOTAL LOSSES"
          value={analytics?.totalLosses?.toString() || "0"}
          color="red"
        />
        <StatCard
          label="AVG BID"
          value={`$${analytics?.avgBidAmount?.toFixed(0) || 0}`}
          color="green"
        />
        <StatCard
          label="AVG WINNING BID"
          value={`$${analytics?.avgWinningBid?.toFixed(0) || 0}`}
          color="green"
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {selectedView === "overview" && (
          <OverviewView analytics={analytics} CustomTooltip={CustomTooltip} />
        )}
        {selectedView === "competitors" && (
          <CompetitorsView competitors={analytics?.topCompetitors || []} />
        )}
        {selectedView === "categories" && (
          <CategoriesView
            data={categoryChartData}
            categories={categories}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Stat Card Component
 */
function StatCard({
  label,
  value,
  color,
}: {
  label: string;
  value: string;
  color: "green" | "yellow" | "red";
}) {
  const colorClasses = {
    green: "border-green-500 text-green-400",
    yellow: "border-yellow-500 text-yellow-400",
    red: "border-red-500 text-red-400",
  };

  return (
    <div className={`border-2 p-3 text-center ${colorClasses[color]}`}>
      <div className="text-xs text-green-600 tracking-wider mb-1">{label}</div>
      <div className="text-xl font-bold">{value}</div>
    </div>
  );
}

/**
 * Overview View - Charts
 */
function OverviewView({
  analytics,
  CustomTooltip,
}: {
  analytics: SpendingAnalytics | null;
  CustomTooltip: any;
}) {
  return (
    <div className="space-y-6">
      {/* Monthly Spending Chart */}
      <div className="border-2 border-green-700 p-4">
        <h3 className="text-green-400 font-bold tracking-wider mb-4">
          MONTHLY SPENDING
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={analytics?.monthlySpend || []}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} />
              <XAxis
                dataKey="month"
                stroke={CHART_COLORS.text}
                tick={{ fill: CHART_COLORS.text, fontSize: 12 }}
              />
              <YAxis
                stroke={CHART_COLORS.text}
                tick={{ fill: CHART_COLORS.text, fontSize: 12 }}
                tickFormatter={(value) => `$${value}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="amount"
                name="Spending"
                stroke={CHART_COLORS.primary}
                strokeWidth={2}
                dot={{ fill: CHART_COLORS.primary }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Domains Won Per Month */}
      <div className="border-2 border-green-700 p-4">
        <h3 className="text-green-400 font-bold tracking-wider mb-4">
          DOMAINS WON PER MONTH
        </h3>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={analytics?.monthlySpend || []}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} />
              <XAxis
                dataKey="month"
                stroke={CHART_COLORS.text}
                tick={{ fill: CHART_COLORS.text, fontSize: 12 }}
              />
              <YAxis
                stroke={CHART_COLORS.text}
                tick={{ fill: CHART_COLORS.text, fontSize: 12 }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="count" name="Domains Won" fill={CHART_COLORS.primary}>
                {analytics?.monthlySpend.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      index === (analytics?.monthlySpend.length || 0) - 1
                        ? CHART_COLORS.primary
                        : CHART_COLORS.secondary
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

/**
 * Competitors View
 */
function CompetitorsView({ competitors }: { competitors: CompetitorProfile[] }) {
  return (
    <div className="space-y-4">
      <div className="border-2 border-green-700 p-4">
        <h3 className="text-green-400 font-bold tracking-wider mb-4">
          TOP COMPETITORS
        </h3>
        {competitors.length === 0 ? (
          <div className="text-center py-8 text-green-600">
            NO COMPETITOR DATA AVAILABLE
          </div>
        ) : (
          <div className="space-y-2">
            {/* Header */}
            <div className="grid grid-cols-5 gap-4 text-xs text-green-600 tracking-wider pb-2 border-b border-green-800">
              <span>BIDDER</span>
              <span>ENCOUNTERS</span>
              <span>AVG BID</span>
              <span>LAST SEEN</span>
              <span>DOMAINS</span>
            </div>
            {/* Rows */}
            {competitors.map((competitor, index) => (
              <div
                key={competitor.accountId}
                className={`grid grid-cols-5 gap-4 text-sm py-2 border-b border-green-900 ${
                  index === 0 ? "text-yellow-400" : "text-green-400"
                }`}
              >
                <span className="font-mono">
                  {index === 0 && "⚠️ "}
                  {competitor.alias}
                </span>
                <span>{competitor.encounterCount}</span>
                <span>${competitor.avgBid.toFixed(0)}</span>
                <span className="text-green-600">
                  {new Date(competitor.lastSeen).toLocaleDateString()}
                </span>
                <span className="text-green-600 truncate">
                  {competitor.domainsCompeted.slice(0, 3).join(", ")}
                  {competitor.domainsCompeted.length > 3 && "..."}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Competitor Frequency Chart */}
      {competitors.length > 0 && (
        <div className="border-2 border-green-700 p-4">
          <h3 className="text-green-400 font-bold tracking-wider mb-4">
            ENCOUNTER FREQUENCY
          </h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={competitors.slice(0, 10)}
                layout="vertical"
                margin={{ left: 100 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={CHART_COLORS.grid} />
                <XAxis
                  type="number"
                  stroke={CHART_COLORS.text}
                  tick={{ fill: CHART_COLORS.text }}
                />
                <YAxis
                  type="category"
                  dataKey="alias"
                  stroke={CHART_COLORS.text}
                  tick={{ fill: CHART_COLORS.text, fontSize: 11 }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#000",
                    border: "2px solid #22c55e",
                  }}
                  labelStyle={{ color: "#4ade80" }}
                />
                <Bar dataKey="encounterCount" fill={CHART_COLORS.warning} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Categories View
 */
function CategoriesView({
  data,
  categories,
}: {
  data: { name: string; value: number; count: number }[];
  categories: DomainCategory[];
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Category Pie Chart */}
      <div className="border-2 border-green-700 p-4">
        <h3 className="text-green-400 font-bold tracking-wider mb-4">
          SPENDING BY CATEGORY
        </h3>
        {data.length === 0 ? (
          <div className="text-center py-8 text-green-600">
            NO CATEGORY DATA AVAILABLE
          </div>
        ) : (
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  label={({ name, percent }) =>
                    `${name} (${((percent ?? 0) * 100).toFixed(0)}%)`
                  }
                  labelLine={{ stroke: CHART_COLORS.text }}
                >
                  {data.map((_, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={PIE_COLORS[index % PIE_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value: number) => [`$${value.toLocaleString()}`, "Spent"]}
                  contentStyle={{
                    backgroundColor: "#000",
                    border: "2px solid #22c55e",
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Category Breakdown Table */}
      <div className="border-2 border-green-700 p-4">
        <h3 className="text-green-400 font-bold tracking-wider mb-4">
          CATEGORY BREAKDOWN
        </h3>
        {data.length === 0 ? (
          <div className="text-center py-8 text-green-600">
            NO CATEGORY DATA AVAILABLE
          </div>
        ) : (
          <div className="space-y-2">
            {/* Header */}
            <div className="grid grid-cols-4 gap-4 text-xs text-green-600 tracking-wider pb-2 border-b border-green-800">
              <span>CATEGORY</span>
              <span>DOMAINS</span>
              <span>SPENT</span>
              <span>AVG</span>
            </div>
            {/* Rows */}
            {data.map((item, index) => {
              const category = categories.find((c) => c.name === item.name);
              return (
                <div
                  key={item.name}
                  className="grid grid-cols-4 gap-4 text-sm py-2 border-b border-green-900"
                >
                  <span className="flex items-center gap-2">
                    <div
                      className="w-3 h-3"
                      style={{
                        backgroundColor:
                          category?.color || PIE_COLORS[index % PIE_COLORS.length],
                      }}
                    />
                    <span className="text-green-400">{item.name}</span>
                  </span>
                  <span className="text-green-400">{item.count}</span>
                  <span className="text-green-400">
                    ${item.value.toLocaleString()}
                  </span>
                  <span className="text-green-600">
                    ${item.count > 0 ? (item.value / item.count).toFixed(0) : 0}
                  </span>
                </div>
              );
            })}
            {/* Total row */}
            <div className="grid grid-cols-4 gap-4 text-sm py-2 font-bold text-green-300">
              <span>TOTAL</span>
              <span>{data.reduce((sum, d) => sum + d.count, 0)}</span>
              <span>
                ${data.reduce((sum, d) => sum + d.value, 0).toLocaleString()}
              </span>
              <span>-</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Analytics;
